/**
 * WorkflowArtifactsSection — unified artifact list for completed workflow
 * instances. Replaces both:
 *   - ora's `ArtifactsSection` in WorkflowDetailDrawer.tsx
 *   - the add-in's `ArtifactsList`
 *
 * `variant="compact"` renders text-only download links (ora style).
 * `variant="rich"` renders icons (plugin style; no thumbnails since story 25240).
 *
 * No fetch — parents own the data (transports differ between apps).
 * The loading skeleton uses CSS-only pulse divs so there is no dependency
 * on app-specific Skeleton components.
 *
 * Prefer ``WorkflowArtifactsGallery`` for new call sites — it bundles this
 * section with ``ArtifactLightbox``, click wiring, and optional self-fetch.
 *
 * Import alias in ora:     ~/common/workflows/components/WorkflowArtifactsSection
 * Import alias in add-in:  @product-app/common/workflows/components/WorkflowArtifactsSection
 */

import DownloadIcon from "@mui/icons-material/Download";
import { IconButton } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { useRef, useState, type ReactElement } from "react";
import CustomTooltip from "~/common/components/CustomTooltip";
import { isStoredFileUrl, isUnavailableStoredUrl } from "~/common/files/fileRoute";
import { downloadStoredFile } from "~/common/files/storedFileFetcher";

export interface WorkflowArtifact {
  kind: string;
  url: string;
  size_bytes?: number | null;
  step_name?: string | null;
  filename?: string | null;
  expires_at?: string | null;
  /** Workflow-supplied human-readable label (e.g. captured page title). */
  note?: string | null;
  /** ISO 8601 timestamp with America/New_York offset. */
  captured_at?: string | null;
  // -- api_evidence summary fields, lifted from the JSON payload by the
  //    artifact-list endpoint (tools/artifacts.py:_read_api_evidence_summary).
  //    Present only when ``kind === "api_evidence"``.
  outcome?: string | null;
  outcome_status?: "ok" | "error" | string | null;
  /** "epic", "graph", or "phase" (phase markers render as inline dividers). */
  category?: string | null;
  duration_ms?: number | null;
  epic_attachment_id?: string | null;
  epic_note_id?: string | null;
  graph_message_id?: string | null;
  graph_attachment_id?: string | null;
  /**
   * Full parsed api_evidence JSON content, embedded by the substrate /
   * forwarded by Product.Api. Present only when ``kind === "api_evidence"``
   * AND the on-disk file is below the substrate's embedding size cap.
   *
   * Why: ``ApiEvidenceView`` renders it inline, with the same
   * ``FormattedJsonView`` the Result section uses, and skips a read of
   * the file. Without it the view reads the JSON through the signed-in
   * /api/files route (story 25240).
   */
  payload?: Record<string, unknown> | null;
}

export interface WorkflowArtifactsSectionProps {
  artifacts: WorkflowArtifact[];
  loading?: boolean;
  /**
   * "compact" = text-only links (ora drawer).
   * "rich"    = emoji icons (plugin drawer).
   * "strip"   = horizontal scroll of icon tiles (HITL Decisions snapshot).
   *             No labels — uses tooltips for context.
   * Defaults to "compact".
   */
  variant?: "compact" | "rich" | "strip";
  /** Optional heading. When absent/empty no heading is rendered. */
  heading?: string;
  /**
   * Optional click handler. When provided, screenshot rows become buttons that
   * fire this callback with the artifact's index — typically wired to open an
   * <ArtifactLightbox>. When absent, rows keep the legacy download-link UX.
   */
  onArtifactClick?: (index: number) => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const KIND_LABEL: Record<string, string> = {
  screenshot: "Screenshot",
  dom: "DOM dump",
  dom_dump: "DOM dump",
  trace: "Playwright trace",
  audit: "Audit packet",
  log: "Log",
  api_evidence: "API call",
  csv: "CSV export",
  bulk_proposals_csv: "Bulk proposals (CSV)",
};

/**
 * Produce a human-readable label for a trace artifact based on its filename
 * stem (``step_name``).
 *
 * - ``trace-<token>.zip``         → "Epic session (trace)"
 * - ``trace-<token>-popup1.zip``  → "Carrier portal popup (trace 1)"
 * - ``trace-<token>-popup2.zip``  → "Carrier portal popup (trace 2)"
 *
 * Applied only when the artifact has no explicit ``note`` from the substrate.
 */
function traceLabel(stepName: string | null | undefined): string {
  if (!stepName) return KIND_LABEL.trace;
  const popupMatch = stepName.match(/-popup(\d+)$/);
  if (popupMatch) {
    return `Carrier portal popup (trace ${popupMatch[1]})`;
  }
  // Primary trace — first BrowserContext, which covers the Epic session.
  if (/^trace/.test(stepName)) {
    return "Epic session (trace)";
  }
  return KIND_LABEL.trace;
}

const KIND_ICON: Record<string, string> = {
  screenshot: "🖼",
  dom: "🧾",
  dom_dump: "🧾",
  trace: "🎞",
  audit: "🛡",
  log: "📜",
  api_evidence: "🔌",
  csv: "📊",
  bulk_proposals_csv: "📊",
};

/**
 * Is this artifact a phase-boundary marker emitted by
 * ``record_phase_boundary(...)``? Phase markers are stored as
 * ``api_evidence`` records with ``category === "phase"`` and have no
 * inspectable body — they render as inline section dividers in the list
 * and stay out of the lightbox gallery entirely.
 *
 * The ``category`` lift only happens on the FS-sourced artifact path
 * (``_read_api_evidence_summary``); the blob-listing path leaves
 * ``category`` off the record unless it was attached as blob metadata at
 * spill time. To stay robust to either source, we also recognise the
 * underlying file-name pattern via the prettified ``step_name`` — the
 * substrate writes ``NN_phase_enter_<phase>.json`` /
 * ``NN_phase_exit_<phase>.json`` which the artifact lister prettifies to
 * ``"NN - Phase Enter <Phase>"``.
 */
const PHASE_STEP_NAME_RE = /\bphase\s+(enter|exit)\b/i;
export function isPhaseMarker(a: WorkflowArtifact): boolean {
  if (a.kind !== "api_evidence") return false;
  if (a.category === "phase") return true;
  return !!a.step_name && PHASE_STEP_NAME_RE.test(a.step_name);
}

/**
 * Is this artifact something the shared ``ArtifactLightbox`` can preview
 * inline? Used by both ora and the Outlook plugin to derive the gallery
 * subset they pass to the modal — keeps both surfaces in lock-step.
 */
export function isLightboxViewable(a: WorkflowArtifact): boolean {
  if (a.kind === "screenshot") return true;
  if (a.kind === "dom" || a.kind === "dom_dump") return true;
  if (a.kind === "api_evidence") return !isPhaseMarker(a);
  return false;
}

/** Same wording as StoredFileLink for a storage URL with no source. */
const UNAVAILABLE_TITLE = "This file is no longer available";

/** A non-stored URL (a file:// fallback written when a spill failed) keeps the plain-link behaviour. */
function openPlainLink(a: WorkflowArtifact): void {
  const link = document.createElement("a");
  link.href = a.url;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  link.download = a.filename ?? "";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

interface ArtifactDownload {
  pending: boolean;
  failed: boolean;
  start: () => void;
}

/**
 * Save an artifact to disk (story 25240). A stored file is buffered through the
 * signed-in /api/files route before the save prompt appears (trace zips are
 * many MB), so the row shows it is busy, ignores repeat clicks, and says
 * "Download failed" the way StoredFileLink does; a successful retry clears it.
 * A storage URL with no source is never opened (its row renders unavailable).
 */
function useArtifactDownload(a: WorkflowArtifact): ArtifactDownload {
  const [pending, setPending] = useState(false);
  const [failed, setFailed] = useState(false);
  const inFlight = useRef(false);
  const start = () => {
    if (!isStoredFileUrl(a.url)) {
      if (!isUnavailableStoredUrl(a.url)) openPlainLink(a);
      return;
    }
    if (inFlight.current) return;
    inFlight.current = true;
    setPending(true);
    setFailed(false);
    void downloadStoredFile(a.url, a.filename)
      .then(
        () => setFailed(false),
        () => setFailed(true),
      )
      .finally(() => {
        inFlight.current = false;
        setPending(false);
      });
  };
  return { pending, failed, start };
}

/** Does a click on the row open the parent's lightbox (rather than download)? */
function opensLightbox(a: WorkflowArtifact, onArtifactClick: ((index: number) => void) | undefined): boolean {
  return isLightboxViewable(a) && !!onArtifactClick;
}

/**
 * Dispatch a click on an artifact row to the right action based on the
 * artifact's `kind`. Centralized so all variants stay in sync:
 *   - screenshot / api_evidence / dom / dom_dump → call onArtifactClick(index)
 *     so the parent opens its lightbox (the shared ArtifactLightbox now
 *     renders bodies for all four kinds via a kind-aware body switch)
 *   - trace → download the .zip (open it at trace.playwright.dev yourself — we
 *     no longer hand that site a link, story 25240)
 *   - else  → download
 */
function handleArtifactClick(
  a: WorkflowArtifact,
  index: number,
  onArtifactClick: ((index: number) => void) | undefined,
  download: ArtifactDownload,
) {
  if (isLightboxViewable(a) && onArtifactClick) {
    onArtifactClick(index);
    return;
  }
  download.start();
}

/** The download control's icon: a spinner while the file is on its way. */
function DownloadControlIcon({ pending }: { pending: boolean }) {
  return pending ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon fontSize="small" />;
}

/** A download glyph inside rows whose click downloads. */
function DownloadGlyph() {
  return (
    <svg
      className="w-4 h-4 text-primary-gray-light-400 flex-shrink-0"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      data-testid="artifact-download-glyph"
    >
      <path d="M12 3v12" />
      <path d="M7 10l5 5 5-5" />
      <path d="M5 21h14" />
    </svg>
  );
}

/**
 * Format an ISO 8601 timestamp (with America/New_York offset from the
 * substrate) for display. Renders as e.g. "May 6, 2026 · 11:32 AM ET".
 * Returns null when the input is missing or unparseable so callers can
 * conditionally hide the row.
 */
export function formatCapturedAt(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZoneName: "short",
  });
  // Intl produces "May 6, 2026, 11:32 AM EDT" — replace the comma between
  // year and time with a middle dot for typographic consistency with the
  // existing artifact row dividers (" · ").
  return formatter.format(date).replace(/(\d{4}),\s/, "$1 · ");
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ---------------------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------------------

function LoadingSkeleton({ heading }: { heading?: string }) {
  return (
    <div
      className="flex flex-col gap-2 mt-2"
      data-testid="artifacts-section-loading"
      aria-label={`${heading || "Artifacts"} (loading)`}
    >
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="h-10 rounded-md bg-primary-gray-light-100 animate-pulse"
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Empty state
// ---------------------------------------------------------------------------

function EmptyState({ heading }: { heading?: string }) {
  return (
    <div
      className="rounded-md border border-dashed border-primary-gray-light-300 bg-primary-gray-light-25 px-3 py-4 text-center mt-2"
      data-testid="artifacts-section-empty"
      aria-label={heading || "Artifacts"}
    >
      <p className="text-xs text-primary-gray-light-500">
        No artifacts captured for this run.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Compact item (ora style)
// ---------------------------------------------------------------------------

function CompactItem({
  a,
  index,
  onClick,
}: {
  a: WorkflowArtifact;
  index: number;
  onClick?: (index: number) => void;
}) {
  const isApiEvidence = a.kind === "api_evidence";
  // For API-evidence rows the most useful row label is the human-readable
  // ``outcome`` lifted from the JSON ("Created attachment att-A …"). Note
  // and step_name fall back to the legacy cascade for everything else.
  const primaryLabel =
    (isApiEvidence && a.outcome?.trim()) ||
    a.note?.trim() ||
    a.filename ||
    (a.kind === "trace" ? traceLabel(a.step_name) : a.step_name) ||
    KIND_LABEL[a.kind] ||
    a.kind;
  const capturedAtDisplay = formatCapturedAt(a.captured_at);
  const sizeLabel = a.size_bytes ? formatBytes(a.size_bytes) : null;
  const durationLabel =
    isApiEvidence && typeof a.duration_ms === "number"
      ? `${a.duration_ms} ms`
      : null;
  const categoryLabel = isApiEvidence && a.category ? a.category : null;
  const failedLabel =
    isApiEvidence && a.outcome_status && a.outcome_status !== "ok"
      ? "failed"
      : null;
  const secondary = [
    failedLabel,
    categoryLabel,
    capturedAtDisplay,
    durationLabel,
    sizeLabel,
  ]
    .filter(Boolean)
    .join(" · ");
  const download = useArtifactDownload(a);
  const unavailable = isUnavailableStoredUrl(a.url);
  const rowBusy = download.pending && !opensLightbox(a, onClick);

  // Outer tile chrome — kept identical across all rows (border, hover, spacing)
  // so every artifact tile has the same outer dimensions regardless of which
  // action elements live inside.
  const tileBase =
    "flex items-stretch rounded-md border border-primary-gray-light-200 bg-base-white text-sm w-full";
  const tileClasses = `${tileBase} hover:border-sunrise hover:shadow-sm transition-all duration-200`;
  const secondaryClasses = "block truncate text-xs font-normal";

  const Body = (
    <>
      <span
        aria-hidden="true"
        className="w-9 h-9 flex items-center justify-center text-lg bg-primary-gray-light-50 rounded flex-shrink-0"
      >
        {KIND_ICON[a.kind] ?? "📎"}
      </span>
      <span className="flex-1 min-w-0">
        <span
          className={`block truncate font-medium ${unavailable ? "text-primary-gray-light-500" : "text-primary-gray-light-900"}`}
        >
          {primaryLabel}
        </span>
        {unavailable ? (
          <span className={`${secondaryClasses} text-primary-gray-light-500`}>File unavailable</span>
        ) : download.pending ? (
          <span className={`${secondaryClasses} text-primary-gray-light-500`}>Downloading…</span>
        ) : download.failed ? (
          <span role="alert" className={`${secondaryClasses} text-primary-error-700`}>
            Download failed
          </span>
        ) : (
          secondary && (
            <span className={`${secondaryClasses} text-primary-gray-light-500`}>{secondary}</span>
          )
        )}
      </span>
    </>
  );

  // A storage URL with no source: shown, never clickable (same as StoredFileLink).
  if (unavailable) {
    return (
      <div className={tileBase} title={UNAVAILABLE_TITLE} data-testid={`artifact-item-${index}`}>
        <span className="flex-1 min-w-0 flex items-center gap-3 px-3 py-2 text-left">{Body}</span>
      </div>
    );
  }

  // Every other row is a button: previewable rows open the lightbox (when
  // wired), everything else downloads through the file route. Screenshots skip
  // the secondary download icon; the other kinds get it as an explicit fallback.
  const showDownloadIcon = a.kind !== "screenshot";
  return (
    <div className={tileClasses} data-testid={`artifact-item-${index}`}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          handleArtifactClick(a, index, onClick, download);
        }}
        disabled={rowBusy}
        aria-busy={rowBusy || undefined}
        className="flex-1 min-w-0 flex items-center gap-3 px-3 py-2 text-left rounded-l-md focus-visible:outline focus-visible:outline-1 focus-visible:outline-sunrise disabled:cursor-progress"
      >
        {Body}
      </button>
      {showDownloadIcon && (
        <CustomTooltip
          id={`artifact-item-${index}-download`}
          title="Download"
          placement="top"
          arrow
          isOnModal
        >
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              download.start();
            }}
            disabled={download.pending}
            aria-busy={download.pending || undefined}
            aria-label={`Download ${primaryLabel}`}
            className="!self-center !mr-1.5 !text-primary-gray-light-500 hover:!text-primary-gray-light-800"
            data-testid={`artifact-item-${index}-download`}
          >
            <DownloadControlIcon pending={download.pending} />
          </IconButton>
        </CustomTooltip>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Rich item (plugin style — icon rows)
// ---------------------------------------------------------------------------

function RichItem({
  a,
  index,
  onClick,
}: {
  a: WorkflowArtifact;
  index: number;
  onClick?: (index: number) => void;
}) {
  const kindLabel = KIND_LABEL[a.kind] ?? a.kind;
  const icon = KIND_ICON[a.kind] ?? "📎";
  const isScreenshot = a.kind === "screenshot";
  const isApiEvidence = a.kind === "api_evidence";
  const primaryLabel =
    (isApiEvidence && a.outcome?.trim()) ||
    a.note?.trim() ||
    (a.kind === "trace" ? traceLabel(a.step_name) : a.step_name?.trim()) ||
    kindLabel;
  const stepLabel =
    !isApiEvidence && a.note?.trim() && a.step_name?.trim()
      ? ` · ${a.step_name}`
      : "";
  const sizeLabel = a.size_bytes ? ` · ${formatBytes(a.size_bytes)}` : "";
  const durationLabel =
    isApiEvidence && typeof a.duration_ms === "number"
      ? ` · ${a.duration_ms} ms`
      : "";
  const capturedAtDisplay = formatCapturedAt(a.captured_at);
  const download = useArtifactDownload(a);
  const unavailable = isUnavailableStoredUrl(a.url);
  const rowBusy = download.pending && !opensLightbox(a, onClick);

  const Body = (
    <>
      <span
        aria-hidden="true"
        className="w-9 h-9 flex items-center justify-center text-lg bg-primary-gray-light-50 rounded flex-shrink-0"
      >
        {icon}
      </span>
      <div className="flex-1 min-w-0">
        <p
          className={`text-xs font-semibold ${unavailable ? "text-primary-gray-light-500" : "text-primary-gray-light-900"}`}
        >
          {primaryLabel}
          {stepLabel && (
            <span className="text-primary-gray-light-500 font-normal">
              {stepLabel}
            </span>
          )}
        </p>
        {unavailable ? (
          <p className="text-2xs text-primary-gray-light-500">File unavailable</p>
        ) : download.pending ? (
          <p className="text-2xs text-primary-gray-light-500">Downloading…</p>
        ) : download.failed ? (
          <p role="alert" className="text-2xs text-primary-error-700">
            Download failed
          </p>
        ) : (
          <p className="text-2xs text-primary-gray-light-500">
            {capturedAtDisplay
              ? `${capturedAtDisplay}${durationLabel}${sizeLabel}`
              : `Download${durationLabel}${sizeLabel}`}
          </p>
        )}
      </div>
      {!onClick && !unavailable && <DownloadGlyph />}
    </>
  );

  const rowBase =
    "flex items-center gap-2.5 rounded-md border border-primary-gray-light-200 bg-base-white px-3 py-2 text-left w-full";
  const rowClasses = `${rowBase} hover:border-sunrise hover:shadow-sm transition-all duration-200`;

  // A storage URL with no source: shown, never clickable (same as StoredFileLink).
  if (unavailable) {
    return (
      <div className="flex items-stretch gap-1" data-testid={`artifact-${a.kind}`}>
        <div className={`${rowBase} flex-1 min-w-0`} title={UNAVAILABLE_TITLE}>
          {Body}
        </div>
      </div>
    );
  }

  const showDownloadIcon = !isScreenshot;
  return (
    <div
      className="flex items-stretch gap-1"
      data-testid={`artifact-${a.kind}`}
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          handleArtifactClick(a, index, onClick, download);
        }}
        disabled={rowBusy}
        aria-busy={rowBusy || undefined}
        className={`${rowClasses} flex-1 min-w-0 disabled:cursor-progress`}
      >
        {Body}
      </button>
      {showDownloadIcon && (
        <CustomTooltip
          id={`artifact-${a.kind}-download`}
          title="Download"
          placement="top"
          arrow
          isOnModal
        >
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              download.start();
            }}
            disabled={download.pending}
            aria-busy={download.pending || undefined}
            aria-label={`Download ${primaryLabel}`}
            className="!text-primary-gray-light-500 hover:!text-primary-gray-light-800"
            data-testid={`artifact-${a.kind}-download`}
          >
            <DownloadControlIcon pending={download.pending} />
          </IconButton>
        </CustomTooltip>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Strip item (HITL Decisions snapshot — horizontal scroll of icon tiles)
// ---------------------------------------------------------------------------

function StripItem({
  a,
  index,
  onClick,
}: {
  a: WorkflowArtifact;
  index: number;
  onClick?: (index: number) => void;
}) {
  const primaryLabel =
    a.note?.trim() ||
    a.filename ||
    a.step_name ||
    KIND_LABEL[a.kind] ||
    a.kind;
  const capturedAtDisplay = formatCapturedAt(a.captured_at);
  const sizeLabel = a.size_bytes ? formatBytes(a.size_bytes) : null;
  const download = useArtifactDownload(a);
  const unavailable = isUnavailableStoredUrl(a.url);
  const tileBusy = download.pending && !opensLightbox(a, onClick);
  // Tooltip mirrors the compact-row info so hovering a strip thumbnail
  // gives the same context the row form would.
  const tooltip = [primaryLabel, unavailable ? "File unavailable" : capturedAtDisplay, sizeLabel]
    .filter(Boolean)
    .join(" · ");

  // Outer chrome stays consistent: 56px (50px image + 3px border each side)
  // square-ish tile. Non-image kinds get the kind icon centered.
  const tileBase =
    "flex-shrink-0 inline-flex flex-col items-center justify-center h-[50px] w-[88px] rounded border border-primary-gray-light-200 bg-primary-gray-light-50 overflow-hidden";
  const tileClasses = `${tileBase} hover:border-sunrise hover:shadow-sm transition-all duration-200`;
  const icon = (
    <span aria-hidden="true" className={`text-lg ${tileBusy ? "animate-pulse" : ""}`}>
      {KIND_ICON[a.kind] ?? "📎"}
    </span>
  );

  const Body = (
    <>
      {icon}
      {download.failed && !download.pending && (
        <span role="alert" className="text-2xs leading-tight text-primary-error-700">
          Download failed
        </span>
      )}
    </>
  );

  // Name, capture time and size in ora's tooltip, not a native title (story
  // 28633). The tile keeps its own aria-label as its name.
  const withTooltip = (tile: ReactElement) => (
    <CustomTooltip
      id={`artifact-strip-${index}`}
      title={tooltip}
      placement="top"
      arrow
      isOnModal
    >
      {tile}
    </CustomTooltip>
  );

  // A storage URL with no source: shown, never clickable (same as StoredFileLink;
  // the tooltip, not a native title, says why).
  if (unavailable) {
    return withTooltip(
      <span
        role="img"
        aria-label={`${primaryLabel} (unavailable)`}
        data-testid={`artifact-strip-${index}`}
        className={`${tileBase} opacity-60`}
      >
        {icon}
        <span className="text-2xs leading-tight text-primary-gray-light-500">Unavailable</span>
      </span>,
    );
  }

  return withTooltip(
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        handleArtifactClick(a, index, onClick, download);
      }}
      disabled={tileBusy}
      aria-busy={tileBusy || undefined}
      aria-label={primaryLabel}
      data-testid={`artifact-strip-${index}`}
      className={`${tileClasses} disabled:cursor-progress`}
    >
      {Body}
    </button>,
  );
}

// ---------------------------------------------------------------------------
// Phase divider (inline section header rendered in place of a row)
// ---------------------------------------------------------------------------

/**
 * Renders a phase-boundary marker as a horizontal rule + phase name section
 * header. Markers are emitted by ``record_phase_boundary(...)`` as
 * ``api_evidence`` records with ``category === "phase"`` and ``outcome`` of
 * the shape ``"── enter <phase_name>"`` — we strip the leading box-drawing
 * decoration and the "enter " verb to keep the divider compact.
 */
function phaseLabelFor(a: WorkflowArtifact): string {
  const outcome = a.outcome?.trim() ?? "";
  // record_phase_boundary writes "── enter <phase_name>"; strip whatever
  // leading non-letter decoration the substrate used so callers don't have
  // to chase typography across versions.
  const stripped = outcome.replace(/^[^A-Za-z]+/, "");
  const withoutVerb = stripped.replace(/^enter\s+/i, "");
  return (
    withoutVerb ||
    a.step_name?.trim() ||
    a.note?.trim() ||
    "step boundary"
  );
}

function PhaseDivider({
  a,
  index,
}: {
  a: WorkflowArtifact;
  index: number;
}) {
  const label = phaseLabelFor(a);
  return (
    <div
      className="flex items-center gap-2 pt-3 pb-1 select-none"
      data-testid={`artifact-phase-divider-${index}`}
      aria-label={`Step boundary: ${label}`}
    >
      <span className="flex-1 h-px bg-primary-gray-light-200" />
      <span className="text-2xs uppercase tracking-wide font-semibold text-primary-gray-light-500">
        {label}
      </span>
      <span className="flex-1 h-px bg-primary-gray-light-200" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// WorkflowArtifactsSection
// ---------------------------------------------------------------------------

export function WorkflowArtifactsSection({
  artifacts,
  loading = false,
  variant = "compact",
  heading,
  onArtifactClick,
}: WorkflowArtifactsSectionProps) {
  if (loading) return <LoadingSkeleton heading={heading} />;
  if (artifacts.length === 0) return <EmptyState heading={heading} />;

  if (variant === "strip") {
    // Strip is the HITL Decisions snapshot — phase markers have no useful
    // square thumbnail, drop them from this surface entirely.
    const stripArtifacts = artifacts.filter((a) => !isPhaseMarker(a));
    return (
      <div
        className="mt-2"
        data-testid="artifacts-section-strip"
        aria-label={heading || "Artifacts"}
      >
        {heading && (
          <p className="text-xs font-semibold text-primary-gray-light-600 mb-1">
            {heading}
          </p>
        )}
        <div className="flex flex-row gap-2 overflow-x-auto pb-1">
          {stripArtifacts.map((a, i) => (
            <StripItem
              key={`${a.url}-${i}`}
              a={a}
              index={i}
              onClick={onArtifactClick}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col gap-2 mt-2"
      data-testid="artifacts-section-list"
      aria-label={heading || "Artifacts"}
    >
      {heading && (
        <p className="text-xs font-semibold text-primary-gray-light-600 mb-1">
          {heading}
        </p>
      )}
      {artifacts.map((a, i) =>
        isPhaseMarker(a) ? (
          <PhaseDivider key={`${a.url}-${i}`} a={a} index={i} />
        ) : variant === "rich" ? (
          <RichItem
            key={`${a.url}-${i}`}
            a={a}
            index={i}
            onClick={onArtifactClick}
          />
        ) : (
          <CompactItem
            key={`${a.url}-${i}`}
            a={a}
            index={i}
            onClick={onArtifactClick}
          />
        ),
      )}
    </div>
  );
}

export default WorkflowArtifactsSection;
